/**
 * La voix de l'assistant, en deux etages.
 *
 *  1. **Le serveur** rend une voix neuronale francaise (`/api/voix`). C'est
 *     celle qu'on entend en temps normal.
 *  2. **Le navigateur** prend le relais si le serveur tarde ou echoue. Les
 *     voix embarquees sont tres inegales — sur iOS, sans voix « Amelioree »
 *     telechargee, elles sont nasillardes — mais elles sont instantanees et
 *     toujours la.
 *
 * Le repli n'est pas une precaution theorique : c'est lui qui garantit que
 * l'assistant repond *toujours*. En conversation, un silence ne se distingue
 * pas d'une panne, on repete sa phrase, et tout se cree en double.
 *
 * `speechSynthesis` est standard et present partout, mais chacun l'implemente
 * a sa facon, et trois de ces facons cassent une conversation mains libres.
 * Les contournements sont commentes un par un plus bas : ce sont eux, et non
 * l'API, qui justifient ce fichier.
 */

export function parolePossible(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Les voix, mises en cache des que le navigateur les publie.
 *
 * `getVoices()` rend une liste **vide** au premier appel : le chargement est
 * asynchrone et `voiceschanged` arrive apres. Sans cette ecoute, la premiere
 * phrase — celle qui donne le ton — sortait dans la voix par defaut, souvent
 * la plus mauvaise du lot. On ecoute donc des le chargement du module, bien
 * avant qu'on ait quoi que ce soit a dire.
 */
let meilleure: SpeechSynthesisVoice | null = null;


function relireVoix(): void {
  if (!parolePossible()) return;
  try {
    const liste = window.speechSynthesis.getVoices();
    if (liste.length === 0) return;

    meilleure = elire(liste);
  } catch {
    // Certains navigateurs jettent avant le premier geste : on retentera.
  }
}

if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  relireVoix();
  window.speechSynthesis.addEventListener?.('voiceschanged', relireVoix);
}

/**
 * Note une voix. Les ecarts de qualite entre voix d'un meme systeme sont
 * enormes, et le premier resultat de `getVoices()` est rarement le bon.
 *
 * Les noms sont le seul indice exploitable : l'API n'expose aucun indicateur
 * de qualite. « Compact » designe les voix embarquees basse definition d'iOS,
 * celles qui sonnent robotiques ; « enhanced », « premium » et « neural »
 * designent les bonnes, a telecharger dans les reglages.
 */
function noter(v: SpeechSynthesisVoice): number {
  const nom = v.name.toLowerCase();
  const langue = v.lang.replace('_', '-').toLowerCase();
  let note = 0;

  if (langue.startsWith('fr-ca')) note += 100;
  else if (langue.startsWith('fr')) note += 60;
  else return -1; // pas francaise : hors concours

  // Amelie est la voix quebecoise d'iOS, et celle qu'on a retenue a l'usage.
  // Elle passe devant tout le reste quand elle est installee.
  if (nom.includes('amelie') || nom.includes('amélie')) note += 200;

  if (/(enhanced|premium|neural|natural)/.test(nom)) note += 40;
  if (nom.includes('google')) note += 25;
  if (nom.includes('siri')) note += 20;
  // Les « compact » sont les voix bas de gamme d'iOS : audibles, mais c'est
  // exactement celles qu'on cherche a eviter.
  if (nom.includes('compact')) note -= 35;
  if (nom.includes('eloquence')) note -= 45;

  return note;
}

function elire(liste: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  let gagnante: SpeechSynthesisVoice | null = null;
  let meilleureNote = -1;
  for (const v of liste) {
    const note = noter(v);
    if (note > meilleureNote) {
      meilleureNote = note;
      gagnante = v;
    }
  }
  return meilleureNote < 0 ? null : gagnante;
}

function choisirVoix(): SpeechSynthesisVoice | null {
  if (meilleure === null) relireVoix();
  return meilleure;
}

/**
 * Debloque la synthese, a appeler depuis un geste de l'utilisateur.
 *
 * Sur iOS, la premiere phrase n'est prononcee que si `speak()` a deja ete
 * appele pendant une interaction. Sans cette amorce, l'assistant reste muet
 * tout le premier tour — puis se met a parler au second, ce qui donne
 * l'impression d'un bug intermittent alors que c'est une regle fixe.
 */
export function amorcerParole(): void {
  try {
    if (parolePossible()) {
      const silence = new SpeechSynthesisUtterance('');
      silence.volume = 0;
      window.speechSynthesis.speak(silence);
      // Dernier rattrapage si `voiceschanged` n'est jamais passe : sans liste,
      // la premiere phrase sortirait dans la voix par defaut.
      relireVoix();
    }
  } catch {
    // Rien a rattraper : au pire l'assistant s'affiche sans se faire entendre.
  }
  // NE PAS debloquer le lecteur audio ici — voir `debloquerLecteur`.
}

/**
 * Un seul element audio, reutilise pour toute la conversation.
 *
 * iOS n'autorise la lecture programmee que sur un element **deja joue pendant
 * un geste de l'utilisateur**. Un `new Audio()` cree a la reception de chaque
 * reponse serait refuse en silence : la voix neuronale ne sortirait jamais sur
 * iPhone, et l'on croirait le serveur en panne. On en garde donc un seul,
 * debloque des l'appui sur le micro, dont on ne change ensuite que la source.
 */
let lecteur: HTMLAudioElement | null = null;

/** Un WAV vide et valide : de quoi consommer le geste sans faire de bruit. */
const SILENCE = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

/**
 * DESACTIVE — a ne pas rebrancher sans avoir regle ce qui suit.
 *
 * Ce deblocage a **casse le micro sur iPhone**, en production, le 20 septembre
 * 2026 : il joue un son au moment precis ou l'on demarre la reconnaissance, et
 * iOS ne laisse pas les deux cohabiter. Lire de l'audio bascule la session
 * audio du systeme et coupe la reconnaissance vocale — l'assistant n'entendait
 * plus rien, sans la moindre erreur pour le dire.
 *
 * C'est le dilemme complet : iOS n'autorise la lecture programmee que sur un
 * element deja joue pendant un geste, et le seul geste dont on dispose est
 * celui qui ouvre le micro. Il faudra donc un autre geste — un bouton
 * « Ecouter la voix » distinct, par exemple — ou attendre la fin de la
 * reconnaissance pour debloquer.
 *
 * Tant que `lecteur` reste `null`, `demanderVoix` rend `null` immediatement :
 * aucune requete, aucune attente, et l'on parle avec la voix du navigateur
 * exactement comme avant.
 */
function debloquerLecteur(): void {
  if (typeof Audio === 'undefined') return;
  try {
    if (lecteur === null) {
      lecteur = new Audio();
      lecteur.preload = 'auto';
    }
    lecteur.src = SILENCE;
    const essai = lecteur.play();
    // Sur les navigateurs qui rendent une promesse, un refus n'a rien de
    // grave : on retombera simplement sur la voix locale.
    if (essai && typeof essai.catch === 'function') essai.catch(() => undefined);
  } catch {
    lecteur = null;
  }
}

export function taire(): void {
  try {
    if (parolePossible()) window.speechSynthesis.cancel();
  } catch {
    // Idem.
  }
  try {
    lecteur?.pause();
  } catch {
    // Idem.
  }
}

/**
 * Le temps qu'on accorde au serveur avant de parler soi-meme.
 *
 * 2,5 s est le point ou l'attente cesse d'etre percue comme « il reflechit »
 * pour devenir « il est casse ». Passe ce delai la voix locale, moins belle,
 * vaut mieux que la belle voix qui se fait attendre.
 */
const PATIENCE_MS = 1800;

/**
 * La voix du serveur a echoue : on ne la redemande plus de la session.
 *
 * Au 20 septembre 2026, `@cf/myshell-ai/melotts` repond « 3043 » sur ce compte
 * quelle que soit la langue — le modele n'est pas joignable. Sans ce drapeau,
 * chaque tour paierait l'attente avant de retomber sur la voix locale : on
 * aurait ajoute de la lenteur sans ajouter de qualite.
 *
 * Le chemin reste en place et se reactivera tout seul le jour ou le modele
 * repondra : seul le premier tour d'une session tente sa chance.
 */
let neuronaleHorsService = false;

/**
 * Dit une phrase, et rend la main quand elle est finie.
 *
 * La promesse est **toujours** tenue, quoi qu'il arrive : c'est elle qui
 * rouvre le micro. Une promesse en suspens ici n'est pas une phrase muette,
 * c'est une conversation qui s'arrete sans rien dire.
 */
export async function dire(texte: string): Promise<void> {
  const propre = texte.trim();
  if (propre === '') return;

  const audio = await demanderVoix(propre);
  if (audio !== null) {
    const joue = await jouer(audio);
    if (joue) return;
  }
  await direLocalement(propre);
}

/**
 * Demande l'audio au serveur, en abandonnant vite.
 *
 * Rend `null` sur tout probleme — reseau, delai, session expiree, modele
 * sature. L'appelant bascule alors sur la voix locale sans que rien ne se
 * voie a l'ecran.
 */
async function demanderVoix(texte: string): Promise<Blob | null> {
  if (lecteur === null) return null; // lecture impossible : inutile de charger
  if (neuronaleHorsService) return null;

  const abandon = new AbortController();
  const minuterie = window.setTimeout(() => abandon.abort(), PATIENCE_MS);
  try {
    const reponse = await fetch('/api/voix', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ texte }),
      signal: abandon.signal,
    });
    if (!reponse.ok) {
      // 401 exclu : la session a expire, l'app va renvoyer a la connexion et
      // la voix n'y est pour rien.
      if (reponse.status !== 401) neuronaleHorsService = true;
      return null;
    }
    const blob = await reponse.blob();
    if (blob.size === 0) {
      neuronaleHorsService = true;
      return null;
    }
    return blob;
  } catch {
    neuronaleHorsService = true;
    return null;
  } finally {
    window.clearTimeout(minuterie);
  }
}

/** Joue l'audio recu. Rend false si la lecture n'a pas pu commencer. */
function jouer(audio: Blob): Promise<boolean> {
  const el = lecteur;
  if (el === null) return Promise.resolve(false);

  return new Promise<boolean>((resoudre) => {
    const adresse = URL.createObjectURL(audio);
    let fini = false;
    const finir = (ok: boolean) => {
      if (fini) return;
      fini = true;
      window.clearTimeout(secours);
      el.onended = null;
      el.onerror = null;
      URL.revokeObjectURL(adresse);
      resoudre(ok);
    };

    el.onended = () => finir(true);
    el.onerror = () => finir(false);
    // Meme filet que pour la synthese locale : un `ended` qui n'arrive jamais
    // fermerait le micro pour de bon.
    const secours = window.setTimeout(() => finir(true), 3000 + texteApproximatif(audio));

    el.src = adresse;
    try {
      const essai = el.play();
      if (essai && typeof essai.catch === 'function') essai.catch(() => finir(false));
    } catch {
      finir(false);
    }
  });
}

/** Duree grossiere a partir du poids du MP3, pour dimensionner le filet. */
function texteApproximatif(audio: Blob): number {
  // ~4 ko par seconde a 32 kbps : large, et seulement un garde-fou.
  return Math.min(20000, (audio.size / 4000) * 1000);
}

function direLocalement(texte: string): Promise<void> {
  const propre = texte.trim();
  if (!parolePossible() || propre === '') return Promise.resolve();

  return new Promise<void>((resoudre) => {
    let fini = false;
    const finir = () => {
      if (fini) return;
      fini = true;
      window.clearInterval(relance);
      window.clearTimeout(secours);
      resoudre();
    };

    let phrase: SpeechSynthesisUtterance;
    try {
      phrase = new SpeechSynthesisUtterance(propre);
    } catch {
      resoudre();
      return;
    }

    const voix = choisirVoix();
    if (voix) phrase.voice = voix;
    phrase.lang = voix?.lang ?? 'fr-CA';
    // Un peu plus rapide que le defaut : en conversation, un debit trainant se
    // percoit comme une lenteur de l'assistant, alors que ce n'est que du
    // temps de parole. Au-dela de 1.15 l'articulation se degrade.
    phrase.rate = 1.08;
    phrase.onend = finir;
    phrase.onerror = finir;

    // Chrome suspend la synthese au bout d'une quinzaine de secondes quand
    // l'onglet n'a pas le focus. Un pause/resume periodique la relance ; c'est
    // le contournement connu, il n'y a pas d'API pour l'eviter.
    const relance = window.setInterval(() => {
      if (!window.speechSynthesis.speaking) return;
      window.speechSynthesis.pause();
      window.speechSynthesis.resume();
    }, 8000);

    // Dernier filet : sur certaines versions d'iOS, `onend` n'arrive jamais si
    // l'ecran s'eteint pendant la phrase. Sans ce delai, le micro ne se
    // rouvrirait plus du tout.
    const secours = window.setTimeout(finir, 4000 + propre.length * 90);

    try {
      window.speechSynthesis.speak(phrase);
    } catch {
      finir();
    }
  });
}
